// src/types/api.types.ts (Updated Member Types)

/**
 * Updated API Types based on actual API response structure
 */

// Base API Response Structure
export interface ApiResponse<T = any> {
  group: any;
  groupName: any;
  dashboard: any;
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
}

// Error Response Structure
export interface ApiError {
  success: false;
  message: string;
  error?: {
    code: string;
    details?: any;
  };
  timestamp: string;
}

// Detailed Validation Error Structure
export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface ValidationErrorDetails {
  errors: ValidationError[];
  message: string;
  success: boolean;
  timestamp: string;
}

// Enhanced Error for Member Operations
export interface MemberValidationError extends Error {
  validationErrors?: ValidationError[];
  generalMessage?: string;
}

// Pagination structure from API
export interface PaginationInfo {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Emergency Contact structure
export interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

// Region structure - Top level in new hierarchy
export interface Region {
  id: string;
  regionName: string;
  regionCode: string;
  country: string;
  population?: number | null;
  regionalAdminId?: string;
  totalProvinces: number;
  totalCommunes: number;
  totalVillages: number;
  totalGroups: number;
  totalMembers?: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  metadata?: {
    generatedAt?: string;
    codeGenerated?: boolean;
  };
  RegionalAdmin?: User;
  Provinces?: Province[];
}

// Province structure for multi-tenant system
export interface Province {
  id: string;
  provinceName: string;
  provinceCode: string;
  regionId: string;
  country: string;
  provincialAdminId?: string;
  totalCommunes: number;
  totalVillages: number;
  totalGroups: number;
  totalMembers: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  ProvincialAdmin?: User;
  Region?: Region;
}

// Village structure for multi-tenant system
export interface Village {
  id: string;
  villageName: string;
  villageCode: string;
  communeId: string;
  villageAdminId?: string;
  population?: number;
  totalGroups: number;
  totalMembers?: number;
  metadata?: {
    generatedAt: string;
    codeGenerated: boolean;
    parentCommuneCode: string;
  };
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  VillageAdmin?: User;
  Commune?: Commune;
  AvecGroups?: AvecGroup[];
}

// Updated Commune structure for new hierarchy
export interface Commune {
  id: string;
  communeName: string;
  communeCode: string;
  provinceId: string;
  communalAdminId?: string;
  totalVillages: number;
  totalGroups: number;
  totalMembers: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  CommunalAdmin?: User;
  Province?: Province;
}

// AVEC Group structure - Updated for new hierarchy
export interface AvecGroup {
  group: any;
  data: any;
  id: string;
  groupName: string;
  groupCode: string;
  villageId: string;
  communeId: string; // Kept for direct reference
  formationDate: string;
  meetingDay: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  meetingFrequency: 'weekly' | 'bi_weekly' | 'monthly';
  contributionAmount: number;
  solidarityContributionAmount: number;
  groupStatus: 'forming' | 'active' | 'inactive' | 'suspended' | 'dissolved';
  totalMembers: number;
  maxMembers: number;
  groupFundBalance: number;
  solidarityFundBalance: number;
  totalSavings: number;
  totalCreditsOutstanding: number;
  interestRate: number;
  cycleStartDate?: string;
  cycleEndDate?: string;
  currentCycle: number;
  lastMeetingDate?: string;
  nextMeetingDate?: string;
  lateFeeAmount: number;
  createdAt: string;
  updatedAt: string;
  Village?: Village;
  Commune?: Commune;
}

// User data embedded in member response
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  userType: 'staff' | 'member';
  staffRole?: 'super_admin' | 'regional_admin' | 'provincial_admin' | 'communal_admin' | 'village_admin' | 'group_agent';
  assignedRegionId?: string; // Only for regional_admin
  assignedProvinceId?: string; // Only for provincial_admin
  assignedCommuneId?: string; // Only for communal_admin
  assignedVillageId?: string; // Only for village_admin
  assignedGroupId?: string; // Only for group_agent
  status: 'active' | 'inactive' | 'suspended';
  
  // Nested staff information from API
  Staff?: {
    id: string;
    role: 'super_admin' | 'provincial_admin' | 'communal_admin' | 'village_admin' | 'group_agent';
    assigned_province_id?: string;
    assigned_commune_id?: string;
    assigned_village_id?: string;
    assigned_group_id?: string;
    permissions?: UserPermissions;
    [key: string]: any;
  };
  
  // Profile information (duplicate of Staff for some APIs)
  profile?: {
    id: string;
    role: 'super_admin' | 'provincial_admin' | 'communal_admin' | 'village_admin' | 'group_agent';
    assigned_province_id?: string;
    assigned_commune_id?: string;
    assigned_village_id?: string;
    assigned_group_id?: string;
    permissions?: UserPermissions;
    [key: string]: any;
  };
  emailVerified: boolean;
  emailVerificationToken: string | null;
  passwordResetToken: string | null;
  passwordResetExpires: string | null;
  lastLoginAt: string | null;
  loginAttempts: number;
  lockedUntil: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Approved by user (minimal info)
export interface ApprovedByUser {
  firstName: string;
  lastName: string;
}

// Member structure matching the API response - Updated for new hierarchy
export interface Member {
  savingsBalance: number;
  creditBalance: number;
  id: string;
  userId: string;
  memberNumber: string;
  dateOfBirth: string;
  gender: 'M' | 'F' | 'Other';
  address: string;
  city: string;
  region: string;
  villageId: string;
  communeId: string;
  provinceId: string;
  regionId: string;
  country: string;
  nationalId: string;
  primaryActivity: string;
  secondaryActivity: string | null;
  monthlyIncome: string;
  emergencyContact: EmergencyContact;
  memberStatus: 'active' | 'inactive' | 'suspended' | 'pending';
  registrationDate: string;
  approvalDate: string | null;
  approvedBy: string | null;
  profilePicture: string | null;
  notes: string | null;
  joinedThroughReferral: string | null;
  idDocumentDeliveryDate: string | null;
  idDocumentExpirationDate: string | null;
  createdAt: string;
  updatedAt: string;

  User: User;
  Village?: Village;
  Commune?: Commune;
  Province?: Province;
  ApprovedByUser?: ApprovedByUser;
}

// Members API response structure
export interface MembersResponse {
  members: Member[];
}

// Paginated Members Response
export interface PaginatedMembersResponse {
  members: Member[];
  pagination: PaginationInfo;
}

// Member creation/update payload - Updated for new hierarchy
export interface CreateMemberPayload {
  // User account fields
  email?: string;
  password?: string;
  firstName: string;
  lastName: string;
  phone: string;

  // Member profile fields
  dateOfBirth: string;
  gender: 'M' | 'F' | 'Other';
  address: string;
  city: string;
  villageId: string;
  communeId: string;
  provinceId: string;
  regionId: string;
  country: string;
  nationalId: string;
  primaryActivity: string;
  secondaryActivity?: string;
  monthlyIncome: number; // API expects number, not string
  emergencyContact: EmergencyContact;

  // Optional fields
  autoApprove?: boolean;
  notes?: string;
}

// Member creation response structure
export interface CreateMemberResponse {
  member: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    userType: 'member';
    status: 'active';
    emailVerified: boolean;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    lastLoginAt: string | null;
    profile: {
      savingsBalance: number;
      creditBalance: number;
      id: string;
      registrationDate: string;
      userId: string;
      memberNumber: string;
      dateOfBirth: string;
      gender: string;
      address: string;
      city: string;
      region: string;
      country: string;
      nationalId: string;
      primaryActivity: string;
      secondaryActivity: string;
      monthlyIncome: string;
      emergencyContact: EmergencyContact;
      memberStatus: 'active' | 'pending';
      approvedBy: string;
      approvalDate: string;
      notes: string | null;
      createdAt: string;
      updatedAt: string;
      profilePicture: string | null;
      joinedThroughReferral: string | null;
      accounts: Array<{
        id: string;
        accountName: string;
        accountType: string;
        accountCategory: string;
        accountNumber: string;
        balance: string;
      }>;
    };
  };
}

// Form validation types
export interface MemberFormErrors {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  nationalId?: string;
  primaryActivity?: string;
  monthlyIncome?: string;
  emergencyContact?: {
    name?: string;
    phone?: string;
    relationship?: string;
  };
}
// Search parameters for members
export interface MemberSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  region?: string;
  city?: string;
  gender?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Authentication Related Types (keeping existing ones)
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface UserPermissions {
  'staff.read': boolean;
  'staff.create': boolean;
  'staff.update': boolean;
  'members.read': boolean;
  'members.create': boolean;
  'members.update': boolean;
  'members.delete': boolean;
  'credits.read': boolean;
  'credits.create': boolean;
  'credits.update': boolean;
  'credits.approve': boolean;
  'credits.reject': boolean;
  'savings.read': boolean;
  'savings.create': boolean;
  'savings.update': boolean;
  'payments.read': boolean; // NOUVEAU
  'payments.approve': boolean; // NOUVEAU
  'payments.verify': boolean; // NOUVEAU
  'payments.retry': boolean; // NOUVEAU
  'reports.read': boolean;
  'reports.generate': boolean;
  'settings.read': boolean;
  'settings.update': boolean;
  approvalLimits: {
    credit: string;
    savings: string;
    solidarity: string;
    payment: string;
  };
}

export interface StaffProfile {
  id: string;
  userId: string;
  staffNumber: string;
  role: string;
  department: string;
  position: string;
  hireDate: string;
  salary: number | null;
  permissions: UserPermissions;
  workingHours: any | null;
  supervisorId: string | null;
  isActive: boolean;
  terminationDate: string | null;
  terminationReason: string | null;
  emergencyContact: any | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginResponse {
  user: User & {
    Staff?: StaffProfile;
    profile?: StaffProfile;
  };
  tokens: AuthTokens;
}

export interface AuthState {
  user: (User & { Staff?: StaffProfile; profile?: StaffProfile }) | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: UserPermissions | null;
}

export interface Meeting {
  id: string;
  title: string;
  description?: string;
  meetingDate: string;
  location: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  attendanceCount: number;
  totalMembers: number;
  agenda?: string[];
  minutes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}


// Add these types to the end of src/types/api.types.ts

// =====================================================
// SAVINGS TRANSACTION TYPES (ADD TO EXISTING FILE)
// =====================================================

// Transaction metadata
export interface TransactionMetadata {
  ipAddress: string;
  initiatedBy: 'member' | 'staff' | 'system';
  processedAt: string;
}

// User info embedded in Member (for savings transactions)
export interface SavingsUser {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

// Member info embedded in transaction
export interface SavingsMember {
  savingsBalance: number;
  creditBalance: number;
  id: string;
  userId: string;
  memberNumber: string;
  dateOfBirth: string;
  gender: 'M' | 'F' | 'Other';
  address: string;
  city: string;
  region: string;
  country: string;
  nationalId: string;
  primaryActivity: string;
  secondaryActivity: string | null;
  monthlyIncome: string;
  emergencyContact: EmergencyContact;
  memberStatus: 'active' | 'inactive' | 'suspended' | 'pending';
  registrationDate: string;
  approvalDate: string | null;
  approvedBy: string | null;
  profilePicture: string | null;
  notes: string | null;
  joinedThroughReferral: string | null;
  createdAt: string;
  updatedAt: string;
  User: SavingsUser;
}

// Approval user info
export interface ApprovalUser {
  firstName: string;
  lastName: string;
}

// Main savings transaction interface - UPDATE EXISTING ONE
export interface SavingsTransaction {
  id: string;
  memberId: string;
  amount: string; // Comes as string from API
  transactionType: 'deposit' | 'withdrawal';
  date: string;
  description: string;
  reference: string | null;
  paymentMethod: 'cash' | 'mobile_money' | 'bank_transfer' | 'check';
  paymentTransactionId: string | null; // ID of the associated payment transaction
  accountingTransactionId: string | null;
  processedBy: string | null;
  approvedBy: string | null;
  status: 'pending' | 'completed' | 'rejected' | 'cancelled';
  balanceAfter: string | null;
  receiptNumber: string;
  notes: string | null;
  metadata: TransactionMetadata;
  isReversed: boolean;
  reversalReason: string | null;
  reversedAt: string | null;
  reversedBy: string | null;
  createdAt: string;
  updatedAt: string;

  // Related data
  Member: SavingsMember;
  ProcessedByUser: ApprovalUser | null;
  ApprovedByUser: ApprovalUser | null;
}

// API response structure for savings
export interface SavingsResponse {
  success: boolean;
  message: string;
  data: {
    transactions: SavingsTransaction[];
  };
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  timestamp: string;
}

// Search and filter parameters for savings
export interface SavingsSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'pending' | 'completed' | 'rejected' | 'cancelled';
  transactionType?: 'deposit' | 'withdrawal';
  paymentMethod?: 'cash' | 'mobile_money' | 'bank_transfer' | 'check';
  memberId?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  sortBy?: 'date' | 'amount' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Transaction creation payload
export interface CreateSavingsTransactionPayload {
  memberId: string;
  amount: number;
  transactionType: 'deposit' | 'withdrawal';
  date: string;
  description: string;
  reference?: string;
  paymentMethod: 'cash' | 'mobile_money' | 'bank_transfer' | 'check';
  notes?: string;
}

// Transaction approval payload
export interface ApproveTransactionPayload {
  notes?: string;
}

// Transaction rejection payload
export interface RejectTransactionPayload {
  reason: string;
  notes?: string;
}

// Transaction statistics
export interface SavingsStats {
  totalTransactions: number;
  pendingTransactions: number;
  completedTransactions: number;
  rejectedTransactions: number;
  totalDeposits: number;
  totalWithdrawals: number;
  totalAmount: number;
  averageTransaction: number;
  transactionsByMonth: Array<{
    month: string;
    deposits: number;
    withdrawals: number;
    total: number;
  }>;
}

// Add these credit types to src/types/api.types.ts

// =====================================================
// CREDIT TYPES (ADD TO EXISTING FILE)
// =====================================================

// Guarantor structure
export interface Guarantor {
  name: string;
  phone: string;
  relationship: string;
}

// Credit metadata
export interface CreditMetadata {
  ipAddress: string;
  requestedAt: string;
}

// Credit member info (embedded in credit response)
export interface CreditMember {
  savingsBalance: number;
  creditBalance: number;
  id: string;
  userId: string;
  memberNumber: string;
  dateOfBirth: string;
  gender: 'M' | 'F' | 'Other';
  address: string;
  city: string;
  region: string;
  country: string;
  nationalId: string;
  primaryActivity: string;
  secondaryActivity: string | null;
  monthlyIncome: string;
  emergencyContact: EmergencyContact;
  memberStatus: 'active' | 'inactive' | 'suspended' | 'pending';
  registrationDate: string;
  approvalDate: string | null;
  approvedBy: string | null;
  profilePicture: string | null;
  notes: string | null;
  joinedThroughReferral: string | null;
  createdAt: string;
  updatedAt: string;
  User: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

// Main credit application interface
export interface CreditApplication {
  id: string;
  creditNumber: string;
  memberId: string;
  requestedAmount: string; // Comes as string from API
  approvedAmount: string | null;
  interestRate: string | null;
  termInMonths: number;
  purpose: string;
  requestDate: string;
  approvalDate: string | null;
  disbursementDate: string | null;
  maturityDate: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'disbursed' | 'active' | 'completed' | 'defaulted';
  repaymentFrequency: 'monthly' | 'weekly' | 'quarterly';
  collateralType: string | null;
  collateralValue: string | null;
  collateralDescription: string | null;
  guarantors: Guarantor[];
  requestedById: string;
  reviewedBy: string | null;
  approvedBy: string | null;
  disbursedBy: string | null;
  totalRepaid: string;
  outstandingBalance: string;
  monthlyPayment: string | null;
  totalInterest: string;
  totalAmount: string | null;
  lastPaymentDate: string | null;
  nextPaymentDate: string | null;
  daysOverdue: number;
  rejectionReason: string | null;
  notes: string | null;
  metadata: CreditMetadata;
  accountingTransactionId: string | null;
  createdAt: string;
  updatedAt: string;

  // Related data
  Member: CreditMember;
  ApprovedByUser?: ApprovalUser;
}

// API response structure for credits
export interface CreditsResponse {
  success: boolean;
  message: string;
  data: {
    credits: CreditApplication[];
  };
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  timestamp: string;
}

// Search parameters for credits
export interface CreditSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'disbursed' | 'active' | 'completed' | 'defaulted';
  memberId?: string;
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
  sortBy?: 'requestDate' | 'requestedAmount' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Credit approval payload
export interface ApproveCreditPayload {
  approvedAmount: number;
  interestRate: number;
  termInMonths: number;
  notes?: string;
}

// Credit rejection payload
export interface RejectCreditPayload {
  reason: string;
}

// Credit disbursement payload
export interface DisburseCreditPayload {
  disbursementDate: string;
  disbursementMethod: 'cash' | 'bank_transfer' | 'mobile_money' | 'check';
  notes?: string;
}

// Credit statistics
export interface CreditStats {
  totalApplications: number;
  pendingApplications: number;
  approvedApplications: number;
  rejectedApplications: number;
  disbursedCredits: number;
  activeCredits: number;
  completedCredits: number;
  defaultedCredits: number;
  totalRequested: number;
  totalApproved: number;
  totalDisbursed: number;
  totalRepaid: number;
  totalOutstanding: number;
  averageAmount: number;
  approvalRate: number;
  defaultRate: number;
  portfolioAtRisk: number;
}

export interface MemberAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  accountCategory: 'member_savings' | 'member_credits' | string;
  balance: string; // Comes as string from API
  parentAccountId: string | null;
  memberId: string;
  isActive: boolean;
  isSystemAccount: boolean;
  description: string;
  currency: string;
  normalBalance: string;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface MemberAccountsResponse {
  success: boolean;
  message: string;
  data: {
    accounts: MemberAccount[];
  };
  timestamp: string;
}

export interface MemberWithCorrectedBalances extends Member {
  correctedSavingsBalance: number;
  correctedCreditBalance: number;
  accountsLoaded: boolean;
}

// Update payload for member editing
export interface UpdateMemberPayload {
  // User account fields (optional for updates)
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;

  // Member profile fields (optional for updates)
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'Other';
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  nationalId?: string;
  primaryActivity?: string;
  secondaryActivity?: string;
  monthlyIncome?: number;
  emergencyContact?: EmergencyContact;

  // Optional fields
  notes?: string;
}

// Update member response structure
export interface UpdateMemberResponse {
  success: boolean;
  message: string;
  data: {
    member: Member;
  };
  timestamp: string;
}

// Status change payload interface
export interface UpdateMemberStatusPayload {
  memberStatus: 'active' | 'inactive' | 'suspended';
  reason: string;
}

// Status change response interface
export interface UpdateMemberStatusResponse {
  success: boolean;
  message: string;
  data: {
    member: Member;
  };
  timestamp: string;
}

// Status change form interface for UI
export interface StatusChangeForm {
  status: 'active' | 'inactive' | 'suspended';
  reason: string;
}

// Member status options for UI - UPDATED
export const MEMBER_STATUS_OPTIONS = [
  {
    value: 'active' as const,
    label: 'Actif',
    color: 'success',
    icon: 'PlayCircleOutlined',
    description: 'Membre actif pouvant effectuer toutes les opérations'
  },
  {
    value: 'inactive' as const,
    label: 'Inactif',
    color: 'default',
    icon: 'DeleteOutlined',
    description: 'Membre supprimé (soft delete) - ne peut plus effectuer d\'opérations'
  },
  {
    value: 'suspended' as const,
    label: 'Suspendu',
    color: 'error',
    icon: 'StopOutlined',
    description: 'Membre suspendu temporairement'
  },
  {
    value: 'pending' as const,
    label: 'En attente',
    color: 'warning',
    icon: 'ExclamationCircleOutlined',
    description: 'Inscription en attente d\'approbation'
  }
] as const;

// Commune-specific member response types
export interface CommuneMember {
  id: string;
  userId: string;
  memberNumber: string;
  membershipNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  joinedDate: string;
  leftDate: string | null;
  totalSavings: number;
  outstandingLoanAmount: number;
  contributionsPaid: number;
  contributionsMissed: number;
  canTakeLoans: boolean;
  lastContributionDate: string | null;
  idDocumentDeliveryDate: string | null;
  idDocumentExpirationDate: string | null;
  Group: {
    id: string;
    groupName: string;
    groupCode: string;
    Village: {
      id: string;
      villageName: string;
      villageCode: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface CommuneMembersPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface CommuneMembersSummary {
  totalMembers: number;
  activeMembers: number;
  totalGroups: number;
  totalVillages: number;
}

export interface CommuneMembersFilters {
  sortBy: string;
  sortOrder: string;
}

export interface CommuneMembersData {
  communeId: string;
  communeName: string;
  members: CommuneMember[];
  pagination: CommuneMembersPagination;
  summary: CommuneMembersSummary;
  filters: CommuneMembersFilters;
}

export interface CommuneMembersResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    data: CommuneMembersData;
  };
  timestamp: string;
}

// Status transition rules - UPDATED (removed manual transitions to inactive)
export const STATUS_TRANSITIONS = {
  active: ['suspended'], // Can only suspend active members
  suspended: ['active'], // Can reactivate suspended members
  pending: ['active'], // Can approve pending members
  inactive: [] // Cannot manually change from inactive (soft delete only)
} as const;


// Staff permissions structure
export interface StaffPermissions {
  // Common permissions
  'credits.read'?: boolean;
  'members.read'?: boolean;
  'reports.read'?: boolean;
  'savings.read'?: boolean;
  'accounts.read'?: boolean;
  'staff.read'?: boolean;
  'settings.read'?: boolean;
  
  // Create permissions
  'credits.create'?: boolean;
  'members.create'?: boolean;
  'savings.create'?: boolean;
  'staff.create'?: boolean;
  'repayments.create'?: boolean;
  'transactions.create'?: boolean;
  
  // Update permissions
  'credits.update'?: boolean;
  'members.update'?: boolean;
  'savings.update'?: boolean;
  'staff.update'?: boolean;
  'accounts.update'?: boolean;
  'settings.update'?: boolean;
  'repayments.update'?: boolean;
  
  // Special permissions
  'credits.approve'?: boolean;
  'credits.reject'?: boolean;
  'members.delete'?: boolean;
  'reports.generate'?: boolean;
  'repayments.read'?: boolean;
  'transactions.read'?: boolean;
  
  // Approval limits
  approvalLimits: {
    credit?: string | number;
    savings?: string | number;
    solidarity?: string | number;
    repayment?: string | number;
    payment?: string | number; // Added payment approval limit
  };
}

// Staff user info
export interface StaffUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  userType: 'staff';
  status: 'active' | 'inactive' | 'suspended';
  emailVerified: boolean;
  emailVerificationToken: string | null;
  passwordResetToken: string | null;
  passwordResetExpires: string | null;
  lastLoginAt: string | null;
  loginAttempts: number;
  lockedUntil: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// Staff member interface
export interface StaffMember {
  id: string;
  userId: string;
  staffNumber: string;
  role: 'administrator' | 'accountant' | 'credit_agent';
  department: string | null;
  position: string | null;
  hireDate: string;
  salary: number | null;
  permissions: StaffPermissions;
  workingHours: any | null;
  supervisorId: string | null;
  isActive: boolean;
  terminationDate: string | null;
  terminationReason: string | null;
  emergencyContact: any | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  
  // Related data
  User: StaffUser;
  Supervisor: StaffMember | null;
  Subordinates: StaffMember[];
}

// Staff API responses
export interface StaffResponseData {
  staff: StaffMember[];
}

export interface StaffResponse {
  success: boolean;
  message: string;
  data: StaffResponseData;
  pagination: PaginationInfo;
  timestamp: string;
}

export interface CreateStaffResponse {
  success: boolean;
  message: string;
  data: {
    user: StaffUser & {
      profile: StaffMember;
    };
  };
  timestamp: string;
}

export interface UpdateStaffResponse {
  success: boolean;
  message: string;
  data: {
    staff: StaffMember;
  };
  timestamp: string;
}

// Staff creation payload
export interface CreateStaffPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'accountant' | 'credit_agent';
  department?: string;
  position?: string;
  salary?: number;
  notes?: string;
}

// Staff update payload
export interface UpdateStaffPayload {
  firstName?: string;
  lastName?: string;
  phone?: string;
  department?: string;
  position?: string;
  salary?: number;
  notes?: string;
  isActive?: boolean;
  terminationReason?: string;
}

// Staff search parameters
export interface StaffSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: 'administrator' | 'accountant' | 'credit_agent';
  department?: string;
  isActive?: boolean;
  sortBy?: 'firstName' | 'lastName' | 'role' | 'hireDate' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Staff role options
export const STAFF_ROLE_OPTIONS = [
  {
    value: 'accountant' as const,
    label: 'Comptable',
    description: 'Gestion des épargnes, comptes et transactions',
    color: 'blue',
    permissions: ['savings', 'accounts', 'transactions', 'reports']
  },
  {
    value: 'credit_agent' as const,
    label: 'Agent de crédit',
    description: 'Gestion des demandes de crédit et remboursements',
    color: 'green',
    permissions: ['credits', 'repayments', 'members']
  }
] as const;

// Staff status options
export const STAFF_STATUS_OPTIONS = [
  {
    value: 'active' as const,
    label: 'Actif',
    color: 'success',
    description: 'Personnel actif'
  },
  {
    value: 'inactive' as const,
    label: 'Inactif',
    color: 'default',
    description: 'Personnel inactif'
  },
  {
    value: 'suspended' as const,
    label: 'Suspendu',
    color: 'error',
    description: 'Personnel suspendu'
  }
] as const;

// Department options
export const DEPARTMENT_OPTIONS = [
  'Administration',
  'Finance',
] as const;

// =====================================================
// GEOGRAPHICAL HIERARCHY SPECIFIC TYPES
// =====================================================

// Region creation/update interfaces
export interface CreateRegionPayload {
  regionName: string;
  regionCode?: string;
  country: string;
  regionalAdminId?: string;
}

export interface UpdateRegionPayload extends Partial<CreateRegionPayload> {
  isActive?: boolean;
}

// Region search and filter parameters
export interface RegionSearchParams {
  search?: string;
  country?: string;
  isActive?: boolean;
  sortBy?: 'regionName' | 'country' | 'totalProvinces' | 'totalCommunes' | 'totalGroups' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Region API response types
export interface RegionsResponse {
  regions: Region[];
  pagination: PaginationInfo;
}

export interface RegionResponse {
  region: Region;
}

// Province creation/update interfaces
export interface CreateProvincePayload {
  provinceName: string;
  regionId: string;
  provincialAdminId?: string;
}

export interface UpdateProvincePayload extends Partial<CreateProvincePayload> {
  isActive?: boolean;
}

// Province search and filter parameters
export interface ProvinceSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  regionId?: string;
  country?: string;
  isActive?: boolean;
  sortBy?: 'provinceName' | 'regionId' | 'totalCommunes' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Province API responses
export interface ProvincesResponse {
  provinces: Province[];
  pagination: PaginationInfo;
}

export interface ProvinceResponse {
  province: Province;
}

// Village creation/update interfaces
export interface CreateVillagePayload {
  villageName: string;
  villageCode?: string;
  communeId: string;
  villageAdminId?: string;
}

export interface UpdateVillagePayload extends Partial<CreateVillagePayload> {
  isActive?: boolean;
}

// Village search and filter parameters
export interface VillageSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  communeId?: string;
  provinceId?: string;
  regionId?: string;
  isActive?: boolean;
  sortBy?: 'villageName' | 'communeId' | 'totalGroups' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Village API responses
export interface VillagesResponse {
  villages: Village[];
  pagination: PaginationInfo;
}

export interface VillageResponse {
  village: Village;
}

// Updated search parameters for existing entities
export interface CommuneSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  provinceId?: string;
  regionId?: string;
  isActive?: boolean;
  sortBy?: 'communeName' | 'provinceId' | 'totalVillages' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface GroupSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  villageId?: string;
  communeId?: string;
  provinceId?: string;
  regionId?: string;
  status?: 'forming' | 'active' | 'inactive' | 'suspended' | 'dissolved';
  sortBy?: 'groupName' | 'villageId' | 'totalMembers' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Location hierarchy helper interfaces
export interface LocationHierarchy {
  country: string;
  regionId: string;
  provinceId: string;
  communeId: string;
  villageId?: string;
}

export interface LocationHierarchyDisplay {
  country: string;
  regionName: string;
  provinceName: string;
  communeName: string;
  villageName?: string;
}

// Burkina Faso regions reference
export const BURKINA_REGIONS = [
  'Boucle du Mouhoun',
  'Cascades',
  'Centre',
  'Centre-Est',
  'Centre-Nord',
  'Centre-Ouest',
  'Centre-Sud',
  'Est',
  'Hauts-Bassins',
  'Nord',
  'Plateau-Central',
  'Sahel',
  'Sud-Ouest',
] as const;

// Village Analytics Types
export interface VillageSystemAnalytics {
  totalVillages: number;
  villagesWithGroups: number;
  totalGroupsAcrossVillages: number;
  totalMembersAcrossVillages: number;
  topPerformingVillages: VillageOverview[];
}

export interface VillageOverview {
  villageId: string;
  villageName: string;
  villageCode: string;
  communeName: string;
  provinceName: string;
  regionName: string;
  totalGroups: number;
  totalMembers: number;
  totalSavings: number;
}

export interface VillageAnalyticsDashboard {
  village: {
    id: string;
    name: string;
    code: string;
    commune: {
      id: string;
      name: string;
      code: string;
    };
    province: {
      id: string;
      name: string;
      code: string;
    };
    region: {
      id: string;
      name: string;
      code: string;
    };
    totalGroups: number;
  };
  period: string;
  healthScore: {
    overall: number;
    breakdown: {
      groupHealth: number;
      financial: number;
      membership: number;
      diversification: number;
    };
    rating: string;
  };
  overview: {
    groups: {
      totalGroups: number;
      activeGroups: number;
      inactiveGroups: number;
      activationRate: number;
    };
    membership: {
      totalMembers: number;
      newMembersInPeriod: number;
      averageGroupSize: number;
      memberGrowthRate: number;
      memberDensity: number;
    };
    financial: {
      totalSavings: number;
      totalCredits: number;
      totalGroupFunds: number;
      avgSavingsPerMember: number;
      totalPortfolio: number;
      avgPortfolioPerGroup: number;
    };
    timeline: {
      oldestGroup: {
        name: string;
        formationDate: string;
      };
      newestGroup: {
        name: string;
        formationDate: string;
      };
      avgGroupAge: any;
    };
  };
  groupPerformance: VillageGroupPerformance[];
  financial: any;
  membership: any;
  activities: any;
  trends: Record<string, VillageTrend>;
  rankings: any;
  generatedAt: string;
}

export interface VillageGroupPerformance {
  group: {
    id: string;
    name: string;
    code: string;
    status: string;
    formationDate: string;
    maxMembers: number;
  };
  metrics: {
    memberCount: number;
    savingsBalance: number;
    creditBalance: number;
    activityIncome: number;
    contributionRate: number;
    meetingAttendance: number;
    efficiencyScore: number;
    avgSavingsPerMember: number;
    capacityUtilization: number;
  };
}

export interface VillageTrend {
  members: number;
  savings: number;
  credits: number;
  activityIncome: number;
  activeGroups: number;
  date: string;
}

// =====================================================
// COUNTRY ANALYTICS TYPES
// =====================================================

// Country Overview Types
export interface CountryOverview {
  totalRegions: number;
  totalProvinces: number;
  totalCommunes: number;
  totalVillages: number;
  totalGroups: number;
  totalMembers: number;
  lastUpdated: string;
}

export interface RegionBreakdown {
  regionId: string;
  regionName: string;
  regionCode: string;
  totalProvinces: number;
  totalCommunes: number;
  totalVillages: number;
  totalGroups: number;
  totalMembers: number;
  totalSavings: number;
  averageSavingsPerMember: number;
  performanceScore?: number;
}

export interface GeographicDistribution {
  averageGroupsPerRegion: number;
  averageGroupsPerProvince: number;
  averageGroupsPerCommune: number;
  averageGroupsPerVillage: number;
  averageMembersPerGroup: number;
  geographicCoverage: {
    regionsWithGroups: number;
    coveragePercentage: number;
  };
}

export interface MembershipGrowth {
  totalActiveMembers: number;
  growthRate: number;
  membershipHealthScore: number;
}

export interface CountryPortfolioHealth {
  totalOutstanding: number;
  portfolioAtRisk: number;
  healthyPortfolio: number;
}

export interface CountryOverviewData {
  country: CountryOverview;
  regionBreakdown: RegionBreakdown[];
  topPerformingRegions: RegionBreakdown[];
  geographicDistribution: GeographicDistribution;
  membershipGrowth: MembershipGrowth;
  portfolioHealth: CountryPortfolioHealth;
  generatedAt: string;
}

// Country Financial Types
export interface MonthlyGrowthRates {
  savings: number;
  credits: number;
  membership: number;
}

export interface FinancialPortfolioHealth {
  totalOutstanding: number;
  portfolioAtRisk: number;
  defaultRate: number;
  recoveryRate: number;
  healthScore: number;
}

export interface FinancialTrend {
  period: string;
  savings: number;
  credits: number;
  growth: number;
}

export interface PerformanceMetrics {
  savingsPerMember: number;
  creditUtilizationRate: number;
  liquidityRatio: number;
  portfolioGrowthRate: number;
}

export interface RiskMetrics {
  concentrationRisk: string;
  liquidityRisk: string;
  creditRisk: string;
}

export interface CountryFinancialData {
  totalSavings: number;
  totalCredits: number;
  totalGroupFunds: number;
  solidarityFunds: number;
  monthlyGrowthRates: MonthlyGrowthRates;
  portfolioHealth: FinancialPortfolioHealth;
  financialTrends: FinancialTrend[];
  performanceMetrics: PerformanceMetrics;
  riskMetrics: RiskMetrics;
}

// Country Membership Types
export interface GenderDistribution {
  male: number;
  female: number;
}

export interface AgeDistribution {
  ageGroup: string;
  count: number;
}

export interface ActivityDistribution {
  activity: string;
  count: number;
}

export interface Demographics {
  totalMembers: number;
  activeMembers: number;
  newMembersThisMonth: number;
  retentionRate: number;
  genderDistribution: GenderDistribution;
  ageDistribution: AgeDistribution[];
  activityDistribution: ActivityDistribution[];
}

export interface MembershipInsights {
  primaryGrowthDrivers: string[];
  retentionChallenges: string[];
  engagementOpportunities: string[];
}

export interface CountryMembershipData {
  demographics: Demographics;
  membershipTrends: Record<string, any>;
  retentionAnalysis: Record<string, any>;
  engagementAnalysis: Record<string, any>;
  insights: MembershipInsights;
}

// Country Performance Types
export interface NationalAverages {
  [key: string]: any;
}

export interface RegionalComparison {
  regionId: string;
  regionName: string;
  regionCode: string;
  totalProvinces: number;
  totalCommunes: number;
  totalVillages: number;
  totalGroups: number;
  totalMembers: number;
  totalSavings: number;
  averageSavingsPerMember: number;
}

export interface TopPerformers {
  [key: string]: any;
}

export interface BenchmarkInsights {
  strongestRegions: string[];
  improvementAreas: string[];
  keySuccessFactors: string[];
}

export interface CountryPerformanceData {
  nationalAverages: NationalAverages;
  regionalComparisons: RegionalComparison[];
  topPerformers: TopPerformers;
  improvementOpportunities: any[];
  benchmarkInsights: BenchmarkInsights;
}

// Country Trends Types
export interface TrendInsights {
  dominantTrends: string[];
  cyclicalPatterns: string[];
  forecastConfidence: string;
}

export interface CountryTrendsData {
  historicalData: Record<string, any>;
  predictiveInsights: Record<string, any>;
  comparativePeriods: Record<string, any>;
  seasonalAnalysis: Record<string, any>;
  trendInsights: TrendInsights;
}

// Regional Drill-down Types
export interface RegionInfo {
  id: string;
  name: string;
  code: string;
  country: string;
}

export interface RegionRanking {
  rank: number;
  outOf: number;
}

export interface DrilldownCapabilities {
  provinces: any[];
  communes: any[];
  villages: any[];
}

export interface RegionalDrilldownData {
  region: RegionInfo;
  provinceBreakdown: any[];
  membershipAnalysis: Record<string, any>;
  financialAnalysis: Record<string, any>;
  performanceMetrics: Record<string, any>;
  regionRanking: RegionRanking;
  drilldownCapabilities: DrilldownCapabilities;
}

// API Response Wrappers
export interface CountryOverviewResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    data: CountryOverviewData;
  };
  timestamp: string;
}

export interface CountryFinancialResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    data: CountryFinancialData;
  };
  timestamp: string;
}

export interface CountryMembershipResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    data: CountryMembershipData;
  };
  timestamp: string;
}

export interface CountryPerformanceResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    data: CountryPerformanceData;
  };
  timestamp: string;
}

export interface CountryTrendsResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    data: CountryTrendsData;
  };
  timestamp: string;
}

export interface RegionalDrilldownResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    data: RegionalDrilldownData;
  };
  timestamp: string;
}

// =====================================================
// PIN RESET TYPES
// =====================================================

// PIN reset payload interface
export interface PinResetPayload {
  memberId: string;
  reason: string;
}

// PIN reset response interface
export interface PinResetResponse {
  success: boolean;
  message: string;
  data?: {
    resetAt: string;
    resetBy: string;
  };
  timestamp: string;
}

// PIN reset error interface
export interface PinResetError extends Error {
  validationErrors?: ValidationError[];
  generalMessage?: string;
}

// =====================================================
// MEMBERSHIP MANAGEMENT TYPES
// =====================================================

// Group membership role change
export interface MembershipRoleChangePayload {
  role: 'member' | 'agent' | 'treasurer' | 'secretary' | 'president' | 'adjoint' | 'comite_de_credit' | 'comite_d_action' | 'comite_de_gestion_de_conflit';
}

// Member removal from group
export interface MemberRemovalPayload {
  reason: string;
}

// Member transfer between groups
export interface MemberTransferPayload {
  newGroupId: string;
  reason: string;
}

// Member update via membership context
export interface MembershipMemberUpdatePayload extends Partial<UpdateMemberPayload> {
  membershipId: string;
}

// Validation response for member operations
export interface MemberOperationValidation {
  canProceed: boolean;
  warnings: string[];
  errors: string[];
  businessRules: {
    hasOutstandingLoans: boolean;
    outstandingLoanAmount?: number;
    hasUnpaidContributions: boolean;
    isGroupAgent: boolean;
    requiresAgentReassignment: boolean;
  };
}

// Available group for transfer
export interface AvailableGroup {
  id: string;
  groupName: string;
  groupCode: string;
  currentMembers: number;
  maxMembers: number;
  canAcceptTransfer: boolean;
}

// Group membership interface (extended)
export interface GroupMembership {
  id: string;
  groupId: string;
  memberId: string;
  role: 'member' | 'agent' | 'treasurer' | 'secretary' | 'president' | 'adjoint' | 'comite_de_credit' | 'comite_d_action' | 'comite_de_gestion_de_conflit';
  joinedDate: string;
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  leftDate?: string;
  leftReason?: string;
  contributionsPaid: number;
  contributionsMissed: number;
  totalSavings: string;
  totalLoans: string;
  outstandingLoanAmount: string;
  membershipNumber: string;
  canTakeLoans: boolean;
  lastContributionDate?: string;
  emergencyContact: any;
  notes?: string;
  Member: {
    savingsBalance: number;
    creditBalance: number;
    id: string;
    userId: string;
    memberNumber: string;
    dateOfBirth: string;
    gender: string;
    address: string;
    city: string;
    region: string;
    country: string;
    nationalId: string;
    primaryActivity: string;
    secondaryActivity: string;
    monthlyIncome: string;
    emergencyContact: any;
    memberStatus: string;
    registrationDate: string;
    approvalDate: string;
    approvedBy: string;
    profilePicture?: string;
    notes?: string;
    User: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
  };
}

export default {};